// ─── Entrée de duel atomique ──────────────────────────────────────────────────
// Energie -1 ET coffre +1 dans un seul PATCH Supabase.
// Throws si énergie insuffisante ou si le PATCH échoue — le caller doit catcher
// et ne pas lancer le match.
//
// TODO RPC : remplacer par `consume_duel_entry(player_name)` côté Supabase pour
// atomicité vraie (vérification + écriture en une seule transaction DB sans race
// condition entre deux clients simultanés).

import { sb } from "../../../lib/supabase";
import { MAX_ENERGY, REGEN_MS, MATCH_COST, calculateRegeneratedEnergy } from "./energy";
import type { EnergyState } from "./energy";
import { MATCHES_FOR_CHEST } from "./dailyChest";
import type { DailyChestState } from "./dailyChest";

function todayUTC(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export interface ConsumeResult {
  energy: EnergyState;
  chest: DailyChestState;
  newDbDate: string;
}

/**
 * Consomme 1 énergie ET incrémente le coffre quotidien en un seul PATCH Supabase.
 *
 * Throws si :
 *   - le joueur n'est pas trouvé en DB
 *   - l'énergie est insuffisante (après regen)
 *   - le PATCH Supabase échoue (réseau, timeout, erreur HTTP)
 *
 * À appeler APRÈS le matchmaking, juste avant de lancer le match.
 */
export async function consumeDuelEntry(
  playerName: string,
  currentChestState: DailyChestState,
  currentDbDate: string | null | undefined,
  attemptId: string
): Promise<ConsumeResult> {
  const enc = encodeURIComponent(playerName);
  const now = Date.now();
  const today = todayUTC();

  // 1. Charger l'énergie courante depuis Supabase
  console.log(`[Energy][${attemptId}] spend start`);
  const rows = await sb.get(
    `players?name=eq.${enc}&select=duel_energy,duel_energy_updated_at&limit=1`
  );
  const row = rows[0];
  if (!row || row.duel_energy == null || !row.duel_energy_updated_at) {
    throw new Error(`[consumeDuelEntry][${attemptId}] player not found or missing energy columns`);
  }

  // 2. Appliquer la regen
  const updatedMs = new Date(row.duel_energy_updated_at).getTime();
  const elapsed = Math.max(0, now - updatedMs);
  const regen = Math.floor(elapsed / REGEN_MS);
  const currentEnergy = Math.min(MAX_ENERGY, row.duel_energy + regen);

  if (currentEnergy < MATCH_COST) {
    throw new Error(`[consumeDuelEntry][${attemptId}] not enough energy (current: ${currentEnergy})`);
  }

  // 3. Calculer les nouvelles valeurs d'énergie
  const newEnergy = currentEnergy - MATCH_COST;
  let newUpdatedAt: string;
  if (currentEnergy >= MAX_ENERGY) {
    newUpdatedAt = new Date(now).toISOString();
  } else {
    const partialMs = elapsed % REGEN_MS;
    newUpdatedAt = new Date(now - partialMs).toISOString();
  }

  // 4. Calculer les nouvelles valeurs du coffre
  const isToday = currentDbDate === today;
  const newChestMatches = isToday ? currentChestState.matches + 1 : 1;
  const newChestClaimed = isToday ? currentChestState.claimed : false;

  // 5. PATCH unique : énergie + coffre ensemble
  const patch: Record<string, unknown> = {
    duel_energy: newEnergy,
    duel_energy_updated_at: newUpdatedAt,
    duel_chest_matches: newChestMatches,
  };
  if (!isToday) {
    patch.duel_chest_date = today;
    patch.duel_chest_claimed = false;
  }

  console.log(`[Energy][${attemptId}] patch payload`, patch);
  const ok = await sb.patch("players", `name=eq.${enc}`, patch);
  if (!ok) {
    throw new Error(`[consumeDuelEntry][${attemptId}] Supabase PATCH failed`);
  }
  console.log(`[Energy][${attemptId}] spend success`);
  console.log(`[DailyChest][${attemptId}] record success`);

  const energyState = calculateRegeneratedEnergy(newEnergy, newUpdatedAt, now);
  const ready = newChestMatches >= MATCHES_FOR_CHEST && !newChestClaimed;
  const chestState: DailyChestState = {
    matches: Math.min(newChestMatches, MATCHES_FOR_CHEST),
    goal: MATCHES_FOR_CHEST,
    ready,
    claimed: newChestClaimed,
  };

  return { energy: energyState, chest: chestState, newDbDate: today };
}
